import { useEffect, useState } from 'react'
import { loadPlayerProfile, loadPlayerGameweekPicks } from '../api/players'
import {
  emptyOtherPlayerGameweekPicks,
  type OtherPlayerGameweekPicks,
} from '../otherPlayerReveal'
import type { AvatarPresetId } from '../avatarPresets'
import type { ChipLedger, GameweekResult } from '../types'

export interface ViewedPlayer {
  userId: string
  displayName: string
}

export interface ViewedPlayerLiveProfile {
  favouriteTeam: string | null
  avatarPreset: AvatarPresetId | null
  seasonHistory: GameweekResult[]
  /** Their own chip ledger, or null when the server didn't send one. Never
   *  substituted with the viewer's — see `getOtherPlayerProfile`. */
  chipsUsed: ChipLedger | null
}

export interface ViewedPlayerController {
  viewedPlayer: ViewedPlayer | null
  viewPlayer: (userId: string, displayName: string) => void
  clearViewedPlayer: () => void
  /** Live identity for the player currently being viewed, or null while it is absent, failed, or
   *  still belongs to a previously-viewed player. Null renders a blank identity — the profile
   *  never guesses a club or an avatar on someone's behalf. */
  liveProfile: ViewedPlayerLiveProfile | null
  /** The viewed player's picks for `revealGameweek`, or the empty bundle when they are absent,
   *  failed, or belong to a different player/gameweek. */
  revealedPicks: OtherPlayerGameweekPicks
}

/**
 * Everything about "someone else's profile is open": who, their live
 * server-persisted identity, and their picks for the gameweek currently inside the
 * "GW N Calls" reveal window.
 *
 * Both fetches are keyed by what they were fetched for and only applied once that key matches
 * what is on screen, so an in-flight response from a previously-viewed player (or a
 * previous gameweek) is ignored rather than briefly painted. That is why neither piece of state
 * is cleared eagerly on open — a stale value can never be read.
 *
 * @param revealGameweek the gameweek inside the reveal window, or null when none is.
 */
export function useViewedPlayer(revealGameweek: number | null): ViewedPlayerController {
  const [viewedPlayer, setViewedPlayer] = useState<ViewedPlayer | null>(null)
  const [live, setLive] = useState<
    ({ userId: string } & ViewedPlayerLiveProfile) | null
  >(null)
  const [picks, setPicks] = useState<{
    userId: string
    gameweek: number
    picks: OtherPlayerGameweekPicks
  } | null>(null)

  const viewedUserId = viewedPlayer?.userId

  useEffect(() => {
    if (!viewedUserId) return
    let cancelled = false
    loadPlayerProfile(viewedUserId)
      .then((profile) => {
        if (cancelled) return
        setLive({
          userId: viewedUserId,
          favouriteTeam: profile.favouriteTeam,
          avatarPreset: profile.avatarPreset,
          seasonHistory: profile.seasonHistory,
          chipsUsed: profile.chipsUsed ?? null,
        })
      })
      .catch((error) => {
        // Their identity stays blank: no club, no avatar, no stats. There is no substitute for a
        // person's own club, and a silent failure here used to render an invented one.
        console.error('Failed to load the viewed player’s profile', error)
      })
    return () => {
      cancelled = true
    }
  }, [viewedUserId])

  useEffect(() => {
    if (!viewedUserId || revealGameweek === null) return
    let cancelled = false
    loadPlayerGameweekPicks(viewedUserId, revealGameweek)
      .then((loaded) => {
        if (cancelled) return
        setPicks({ userId: viewedUserId, gameweek: revealGameweek, picks: loaded })
      })
      .catch(() => {
        // Leave the empty fallback in place — see emptyOtherPlayerGameweekPicks.
      })
    return () => {
      cancelled = true
    }
  }, [viewedUserId, revealGameweek])

  const matchedLive = live && live.userId === viewedUserId ? live : null
  const matchedPicks =
    picks && picks.userId === viewedUserId && picks.gameweek === revealGameweek
      ? picks.picks
      : emptyOtherPlayerGameweekPicks()

  return {
    viewedPlayer,
    viewPlayer: (userId, displayName) => setViewedPlayer({ userId, displayName }),
    clearViewedPlayer: () => setViewedPlayer(null),
    liveProfile: matchedLive && {
      favouriteTeam: matchedLive.favouriteTeam,
      avatarPreset: matchedLive.avatarPreset,
      seasonHistory: matchedLive.seasonHistory,
      chipsUsed: matchedLive.chipsUsed,
    },
    revealedPicks: matchedPicks,
  }
}
